'use client';

import type React from 'react';
import btnNext from '../../../../assets/images/anime/btn_next.png';
import btnSkip from '../../../../assets/images/anime/btn_skip.png';
import itemNameBaseBlue from '../../../../assets/images/anime/item-name_base_blue.png';
import itemNameBaseGold from '../../../../assets/images/anime/item-name_base_gold.png';
import itemNameBaseRainbow from '../../../../assets/images/anime/item-name_base_rainbow.png';
import itemNameBaseSilver from '../../../../assets/images/anime/item-name_base_silver.png';
import animeBgBlue from '../../../../assets/images/anime/v2/default/blue.png';
import animeFlash from '../../../../assets/images/anime/v2/default/flash.png';
import animeBgGold from '../../../../assets/images/anime/v2/default/gold.png';
import animeBgRainbow from '../../../../assets/images/anime/v2/default/rainbow.png';
import animeBgSilver from '../../../../assets/images/anime/v2/default/silver.png';
import animeBgBlueRaffle from '../../../../assets/images/anime/v2/raffle/blue.png';
import animeFlashRaffle from '../../../../assets/images/anime/v2/raffle/flash.png';
import animeBgGoldRaffle from '../../../../assets/images/anime/v2/raffle/gold.png';
import animeBgRainbowRaffle from '../../../../assets/images/anime/v2/raffle/rainbow.png';
import animeBgSilverRaffle from '../../../../assets/images/anime/v2/raffle/silver.png';
import stagingRankBlue from '../../../../assets/images/staging/sp/blue-543a.png';
import stagingRankGold from '../../../../assets/images/staging/sp/gold-543a.png';
import stagingRankRainbow from '../../../../assets/images/staging/sp/rainbow-543a.png';
import stagingRankSilver from '../../../../assets/images/staging/sp/silver-543a.png';
import ActionButton from '../../../../components/form/ActionButton/ActionButton';
import fontsStyles from '../../../../styles/modules/fonts.module.scss';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import type { VariantType } from '../../../../types/variant';
import type { LotteryDrawResult } from '../../types';
import LotteryResultsTransfer from '../LotteryResultsTransfer/LotteryResultsTransfer';
import styles from './LotteryDrawAnimeV2.module.scss';
import raffleStyles from './LotteryDrawAnimeV2.raffle.module.scss';
import { useLotteryDrawAnimeV2 } from './hooks/useLotteryDrawAnimeV2';

export type LotteryDrawAnimeV2Props = {
  lotteryDrawResult: LotteryDrawResult;
  /** スタイルのバリアント（オプション） */
  variant?: VariantType;
};

// シーン1のローディング表示に添える説明文。動画が再生されるまで画面に変化がなく、
// ユーザーが状況を判断できないため、待てばよいのか操作が必要なのかを文言で伝える。
const LOADING_NOTE_DOWNLOADING = 'ダウンロード処理中です。完了するまでしばらくお待ちください。';
const LOADING_NOTE_UNPLAYABLE = '動画の再生に失敗しました。「抽選結果を見る」ボタンを押して進めてください。';

function LotteryDrawAnimeV2({ lotteryDrawResult, variant = 'default' }: LotteryDrawAnimeV2Props) {
  const currentStyles = variant === 'raffle' ? raffleStyles : styles;

  // シーン2で使う CSS 背景画像の URL（背景・集中線・等賞バッジ）を stagingRank ごとに返す。
  //
  // これらは SCSS の background-image で指定しているため、シーン2が display: none の間は
  // ブラウザーが取得しない。シーン2へ切り替わってから読み始めるぶん表示が遅れ、
  // 「景品は出ているのに背景だけ出ていない」状態になる。それを避けるための先読みに使う
  // （実際の取得は useLotteryDrawAnimeV2 が行う）。
  //
  // **先読みするのは CSS の background-image だけでよい。** シーン2に出る画像でも
  // <img> のもの（景品画像・景品名ラベル台紙 item-name_base_*・次へ btn_next・SKIP btn_skip）は
  // display: none の間もブラウザーが取得するため、演出動画の再生中に読み込みが終わっている。
  // この「<img> は間に合っているが CSS 背景は間に合っていない」差が症状の原因であり、
  // 先読みは前者に後者を合わせるための処置。<img> 側に足す意味はない。
  //
  // SCSS の url() は default / raffle とも9件（背景4 + 集中線1 + 等賞バッジ4）で、
  // 下記を stagingRank ぶんに展開したものと全数一致する。**url() を足したらここにも足すこと。**
  //
  // **SCSS の url() が指すファイルと必ず一致させること。** ずれると CSS 側が別のファイルを
  // 要求して二重ダウンロードになり、先読みが無駄になるどころか遅くなる。しかも表示は正常なので
  // 症状として気づけない。
  // 背景と集中線は variant で参照先が変わる（.module.scss は v2/default、
  // .raffle.module.scss は v2/raffle）。等賞バッジは staging/ に raffle 用が無いため共通。
  //
  // フックへ渡すため、下記の getGoodsNameBaseImage 等より前に定義する必要がある。
  const getScene2ImageUrls = (stagingRank: string) => {
    const isRaffle = variant === 'raffle';

    const backgroundUrl = () => {
      switch (stagingRank) {
        case '1':
          return isRaffle ? animeBgRainbowRaffle.src : animeBgRainbow.src;
        case '2':
          return isRaffle ? animeBgGoldRaffle.src : animeBgGold.src;
        case '3':
          return isRaffle ? animeBgSilverRaffle.src : animeBgSilver.src;
        default:
          return isRaffle ? animeBgBlueRaffle.src : animeBgBlue.src;
      }
    };

    const stagingRankUrl = () => {
      switch (stagingRank) {
        case '1':
          return stagingRankRainbow.src;
        case '2':
          return stagingRankGold.src;
        case '3':
          return stagingRankSilver.src;
        default:
          return stagingRankBlue.src;
      }
    };

    return [backgroundUrl(), isRaffle ? animeFlashRaffle.src : animeFlash.src, stagingRankUrl()];
  };

  const {
    scene,
    videoUrl,
    maxRank,
    currentGoods,
    goResult,
    leaveScene1,
    nextGoods,
    handleStartAnimation,
    handleVideoEnded,
    handleVideoTimeUpdate,
    handleVideoError,
    handleVideoPlaying,
    hasVideoStarted,
    isVideoUnplayable,
    currentGoodsIndex,
    goodsList,
  } = useLotteryDrawAnimeV2(lotteryDrawResult, getScene2ImageUrls);

  // stagingRank に応じた景品名ベース画像を選択する（stagingRank は文字列）
  const getGoodsNameBaseImage = () => {
    switch (currentGoods().stagingRank) {
      case '1':
        return itemNameBaseRainbow.src;
      case '2':
        return itemNameBaseGold.src;
      case '3':
        return itemNameBaseSilver.src;
      default:
        return itemNameBaseBlue.src;
    }
  };

  // stagingRank に応じた背景画像の縦横比（"幅 / 高さ"）を返す（CSS の .rank1/2/3 と同じ画像対応）。
  // PC では CSS 変数 --anime-bg-ratio を aspect-ratio に用いて高さを決めるため、背景画像を差し替えても
  // import した実寸から比率が再計算され、高さが自動で追従する（数値のハードコード不要）。
  const getBackgroundAspectRatio = () => {
    // variant === 'raffle' の場合は raffle 用画像、それ以外は default 用画像の実寸から比率を計算する
    const bgImages =
      variant === 'raffle'
        ? {
            rainbow: animeBgRainbowRaffle,
            gold: animeBgGoldRaffle,
            silver: animeBgSilverRaffle,
            blue: animeBgBlueRaffle,
          }
        : { rainbow: animeBgRainbow, gold: animeBgGold, silver: animeBgSilver, blue: animeBgBlue };

    switch (currentGoods().stagingRank) {
      case '1':
        return `${bgImages.rainbow.width} / ${bgImages.rainbow.height}`;
      case '2':
        return `${bgImages.gold.width} / ${bgImages.gold.height}`;
      case '3':
        return `${bgImages.silver.width} / ${bgImages.silver.height}`;
      default:
        return `${bgImages.blue.width} / ${bgImages.blue.height}`;
    }
  };

  return (
    <>
      {/* 自動再生可否の判定中。白フラッシュを防ぐため全画面の黒バックドロップのみを描画する */}
      {scene === 'detecting' && <div className={currentStyles.sceneDetecting} />}
      {/* 演出開始前の transfer シーン。ボタン押下（ユーザー操作）で scene 1 の動画再生を開始し、自動再生制約を回避する */}
      {scene === 'transfer' && <LotteryResultsTransfer handleStartAnimation={handleStartAnimation} variant={variant} />}
      {scene === 1 && (
        <>
          {/* data-testid は E2E 用。本番ビルドでは CSS Modules のクラス名がハッシュ化され、
              クラス名の部分一致でスクロール対象を特定できないため付与している（scene2 と同じ理由） */}
          {/* .scene1VideoPlaying は動画が正常に再生されている間だけ付ける。付けない間は .scene1 の
              明るい下地（動画が描画されるまでの見え方）が残る。PC は動画の下端の下に下地が露出するため、
              再生中はそこを黒に戻す必要がある（理由の詳細は SCSS 側のコメント）。
              再生に失敗したときは付けない（黒背景だと「抽選結果を見る」ボタンが同化して見えないため）。 */}
          <div
            className={`${currentStyles.sceneContainer} ${currentStyles.scene1} ${
              hasVideoStarted && !isVideoUnplayable ? currentStyles.scene1VideoPlaying : ''
            }`}
            data-testid='scene1'
          >
            {/* 再生が始まるまでのローディング表示。動画の裏（z-index が下）に置き、再生開始で取り除く。
                ダウンロードが完了しない間は表示したままにして、シーン遷移まで画面が固まったように
                見えるのを防ぐ。
                再生できない場合（isVideoUnplayable）は出さない。error が来た時点で再生できないことは
                確定しており「読み込み中」は事実と異なるうえ、下に出る「ボタンを押して進めてください」
                という案内と矛盾して、待てばよいのか操作が必要なのかが分からなくなるため。
                再生開始前に失敗した場合も再生開始後に壊れた場合も同じ扱いにする */}
            {!hasVideoStarted && !isVideoUnplayable && (
              <p className={`${currentStyles.scene1Loading} ${fontsStyles.montserrat}`}>Now Loading</p>
            )}
            {/* 状況の説明文。ローディング表示だけでは待てばよいのか操作が必要なのかが分からないため、
                再生できないと分かった時点で操作を促す文言に切り替える。
                「Now Loading」と違って再生開始後も出す必要がある（hasVideoStarted が true のまま
                error になる、再生途中で壊れた場合）ため、条件を広く取っている */}
            {(!hasVideoStarted || isVideoUnplayable) && (
              <p className={currentStyles.scene1LoadingNote}>
                {isVideoUnplayable ? LOADING_NOTE_UNPLAYABLE : LOADING_NOTE_DOWNLOADING}
              </p>
            )}
            {videoUrl && (
              // GIFが重すぎるためMP4に置き換え。ロード後すぐに再生する（ループしない）。
              // シーン2への遷移は再生終了（onEnded）を起点にする。ハズレは終了の少し手前で遷移させるため
              // 再生位置（onTimeUpdate）も見る。再生できない場合（onError）はタイマーでの遷移に切り替える。
              <video
                className={currentStyles.scene1Video}
                src={videoUrl}
                autoPlay
                muted
                playsInline
                preload='auto'
                onEnded={handleVideoEnded}
                onTimeUpdate={(event) => handleVideoTimeUpdate(event.currentTarget)}
                onError={handleVideoError}
                onPlaying={handleVideoPlaying}
              />
            )}
          </div>
          {/* 動画が再生できない場合（onError）の脱出口。この経路は ended も再生位置の更新も来ないため
              保険タイムアウト（30秒）まで画面が変わらない。それを待たずに抜けられる手段として出す。
              表示条件を再生開始前（!hasVideoStarted）にすると、正常に再生できる端末でも再生が始まる
              までの数百ミリ秒だけボタンが動画に重なって見えるため、error の発火を起点にしている。
              ダウンロードが完了せず error も来ない場合はボタンを出さず、保険タイマー（30秒）に委ねる。
              遷移先は通常経路（leaveScene1）と同じで、あたりはシーン2へ状態遷移し、ハズレは /nothing へ
              ページ遷移する（ハズレを結果画面へ送らない理由は leaveScene1 のコメントを参照）。
              上記スクロールコンテナ（.sceneContainer.scene1）の内側に置くと動画と一緒に
              スクロールして画面外へ出るため、兄弟として置き親（.pageAnime）を基準に絶対配置する */}
          {isVideoUnplayable && (
            <div className={currentStyles.scene1ResultButton}>
              <ActionButton
                variant={variant}
                color='black'
                size='large'
                onClick={leaveScene1}
                dataTestId='scene1-go-result'
              >
                抽選結果を見る
              </ActionButton>
            </div>
          )}
        </>
      )}
      {/* ハズレではない場合、レンダリングをしておかないとアニメーションが動かないため、display:noneで非表示にしている */}
      {maxRank !== '-1' && (
        // data-testid は E2E 用。本番ビルドでは CSS Modules のクラス名がハッシュ化され、
        // クラス名の部分一致でスクロール対象や背景を特定できないため付与している
        <div
          className={`${currentStyles.sceneContainer} ${currentStyles.scene2}`}
          style={{ display: scene === 2 ? 'flex' : 'none' }}
          data-testid='scene2'
        >
          {/* 光の演出(imageFlash)は背景要素の子として配置する。背景要素に overflow: hidden をかけるため、
              背景画像の範囲外に光の演出がはみ出さずクリップされる（はみ出すと scene2 のスクロール量が
              増え、最下部で背景下端の下に黒地が出る） */}
          <div
            className={`${currentStyles.imageBackground} ${currentStyles[`rank${currentGoods().stagingRank}`]}`}
            style={{ '--anime-bg-ratio': getBackgroundAspectRatio() } as React.CSSProperties}
            data-testid='scene2-background'
          >
            <div className={currentStyles.imageFlash} />
          </div>
          {/* テキスト、画像、ボタン */}
          <div className={currentStyles.sceneFront} data-testid='scene2-front'>
            <div className={`${currentStyles.textCongrats} ${fontsStyles.montserrat} ${utilsStyles.textBold}`}>
              &nbsp;
            </div>
            {/* 景品画像（と下の景品名）はフェードインさせる。key は「次へ」で景品を送ったときに
                フェードインを再生し直すためのもの。CSS アニメーションは要素が残ったままだと再生されない
                ため、景品が変わるたびに要素を作り直して先頭から流す。
                シーン2へ入るときの再生に key は関与しない。scene2 の display が none → flex へ変わる
                ことで先頭から始まる（CSS Animations の仕様。描画されていない間はアニメーションが走らず、
                描画された時点で開始する）。**key にシーン番号まで含めないこと。** 入場時にも要素を
                作り直すことになり、シーン1の間に取得し終えている景品画像を捨ててしまう。
                data-testid は E2E 用。フェードインの回帰（基準値が opacity: 1 であること）の検証に使う */}
            <div
              key={`goods-image-${currentGoodsIndex}`}
              className={currentStyles.imageWrapper}
              data-testid='scene2-image-wrapper'
            >
              <img className={currentStyles.goodsImage} src={currentGoods().imageUrl} alt='景品' />
              {/* 演出 */}
              <div className={`${currentStyles.stagingRank} ${currentStyles[`rank${currentGoods().stagingRank}`]}`} />
              <div className={`${currentStyles.stagingText} ${fontsStyles.montserrat}`}>
                {currentGoods().stagingText}
                <small>賞</small>
              </div>
            </div>

            {/* 景品名は台紙画像と景品名テキストを一体でフェードインさせる（key の理由は上記と同じ） */}
            <div
              key={`goods-name-${currentGoodsIndex}`}
              className={currentStyles.goodsNameBanner}
              data-testid='scene2-goods-name-banner'
            >
              <img className={currentStyles.goodsNameImage} src={getGoodsNameBaseImage()} alt='' />
              <p className={`${currentStyles.goodsName} ${utilsStyles.textBold}`}>{currentGoods().name}</p>
            </div>
            {/* 件数表示と下の「次へ」「SKIP」には**フェードインをかけない**。アニメーションが再生されなくても
                進行に必要な操作要素は見えている状態を保つため（SCSS 側のコメントも参照）。
                key も付けない（作り直す理由がないため）。
                data-testid は E2E 用。フェードインが誤ってこちら側へ波及した退行を検知する */}
            <div className={`${currentStyles.goodsDetail} ${utilsStyles.textBold}`} data-testid='scene2-goods-detail'>
              <output className={currentStyles.goodsIndex}>
                {currentGoodsIndex + 1} / {goodsList.length}
              </output>
            </div>
            <div className={currentStyles.buttonArea} data-testid='scene2-button-area'>
              <button type='button' className={currentStyles.nextButton} onClick={nextGoods}>
                <img className={currentStyles.nextButtonImage} src={btnNext.src} alt='次へ' />
              </button>
              <button type='button' className={currentStyles.skipButton} onClick={goResult}>
                <img className={currentStyles.skipButtonImage} src={btnSkip.src} alt='スキップ' />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default LotteryDrawAnimeV2;
